import React from "react";
import classNames from "classnames";
import { TextareaAutosize, TextareaAutosizeProps } from "@mui/material";
import { ButtonV2Color, ButtonV2Size } from "App/core/ButtonV2";
import styles from "./TextInputV2.module.scss";

interface BaseProps {
  label: string;
  hideLabel?: boolean;
  variant?: "outlined" | "solid";
  color?: ButtonV2Color;
  inputSize?: ButtonV2Size;
  shape?: "rounded";
  startComponent?: React.ReactNode;
  endComponent?: React.ReactNode;
  error?: string;
}

interface TextAreaProps
  extends BaseProps,
    Omit<TextareaAutosizeProps, "color"> {
  inputType: "textarea";
}

interface InputProps
  extends BaseProps,
    Omit<React.ComponentProps<"input">, "color"> {
  inputType: "input";
}

type Props = TextAreaProps | InputProps;

const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
};

export const TextInputV2 = ({
  label,
  hideLabel,
  inputType,
  inputSize = "sm",
  className,
  color = "secondary",
  variant = "outlined",
  shape = "rounded",
  startComponent,
  endComponent,
  error,
  ...props
}: Props) => {
  const inputId = props.id || slugify(label);
  const wrapperClassName = classNames(
    styles.inputWrapper,
    error ? styles["danger"] : styles[color],
    styles[variant],
    styles[inputSize],
    styles[shape],
    className,
  );

  const renderInput = () => {
    const inputProps = {
      ...props,
      id: inputId,
      className: styles.input,
      "aria-disabled": props.disabled,
      "aria-invalid": !!error,
      "aria-describedby": error ? `${inputId}-error` : undefined,
      "aria-required": props.required,
      "aria-label": hideLabel ? label : undefined, // Only use aria-label if label is visually hidden
    };

    if (inputType === "textarea") {
      return <TextareaAutosize {...(inputProps as TextAreaProps)} />;
    }
    return <input {...(inputProps as InputProps)} name={inputId} />;
  };

  return (
    <div className={styles.root} role="group" aria-labelledby={inputId}>
      <label
        htmlFor={inputId}
        className={classNames(
          styles.label,
          hideLabel && styles["visually-hidden"],
        )}
        id={inputId}
      >
        {label}
        {props.required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div className={wrapperClassName}>
        {startComponent && (
          <div className={styles.startComponent}>{startComponent}</div>
        )}
        <div className={styles.inputWrapper}>{renderInput()}</div>
        {endComponent && (
          <div className={styles.endComponent}>{endComponent}</div>
        )}
      </div>
      {error && (
        <div
          role="alert"
          id={`${inputId}-error`}
          aria-live="polite"
          className={styles.error}
        >
          {error}
        </div>
      )}
    </div>
  );
};
